// src/components/Common/SelectCustomer.jsx
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { getActiveCustomersLookup } from "../../services/customerService";

/**
 * SelectCustomer
 * --------------
 * Selector de clientes activos (lookup ligero).
 *
 * Basado en CustomerLookupItemDto del backend:
 * - id
 * - fullName
 * - documentNumber
 * - customerTypeName
 * - email
 * - companyName
 *
 * UX:
 * - Jurídico: "NIT {documentNumber} · {companyName}"
 * - Natural: "{documentNumber} · {fullName}"
 * - Búsqueda inteligente: por documento, nombre/razón social, email, tipo.
 *
 * Props:
 * - value: Guid del cliente seleccionado (string) o "".
 * - onChange: (customerId: string) => void
 * - onSelectCustomer?: (customerDto: any | null) => void (opcional)
 * - isDisabled?: boolean
 * - placeholder?: string
 */
const SelectCustomer = ({
  value,
  onChange,
  onSelectCustomer,
  isDisabled = false,
  placeholder = "Seleccione un cliente...",
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const norm = (v) => (v || "").toString().trim().toUpperCase();

  // Detectar modo oscuro
  const checkDarkMode = () =>
    document.body.classList.contains("dark-mode") ||
    document.querySelector(".app")?.classList.contains("dark-mode");

  useEffect(() => {
    setIsDarkMode(checkDarkMode());

    const targetNode = document.querySelector(".app") || document.body;
    const observer = new MutationObserver(() => setIsDarkMode(checkDarkMode()));

    observer.observe(targetNode, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Cargar clientes activos al montar
  useEffect(() => {
    let mounted = true;

    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const res = await getActiveCustomersLookup();

        if (!mounted) return;

        // Tu servicio parece devolver: { success, data: [...] }
        if (res?.success && Array.isArray(res.data)) {
          setCustomers(res.data);
        } else if (Array.isArray(res?.data)) {
          // fallback por si el servicio devuelve data directo
          setCustomers(res.data);
        } else {
          setCustomers([]);
        }
      } catch {
        if (mounted) setCustomers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchCustomers();
    return () => {
      mounted = false;
    };
  }, []);

  // -------------------- Estilos react-select --------------------
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 38,
      backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
      borderColor: state.isFocused
        ? "#6c63ff"
        : isDarkMode
        ? "#444"
        : "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 0.2rem rgba(108, 99, 255, 0.25)"
        : "none",
      color: isDarkMode ? "#fff" : "#000",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
      color: isDarkMode ? "#fff" : "#000",
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? "#fff" : "#000",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#6c63ff"
        : state.isFocused
        ? isDarkMode
          ? "#2c2c3e"
          : "#f0f0f0"
        : isDarkMode
        ? "#1e1e2f"
        : "#fff",
      color: isDarkMode ? "#fff" : "#000",
      cursor: "pointer",
      fontSize: 14,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? "#aaa" : "#888",
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? "#fff" : "#000",
    }),
  };

  // -------------------- Helpers de display --------------------
  const isLegalCustomer = (c) => {
    // Regla alineada al lookup del backend:
    // - Si hay CompanyName => es jurídico
    // - Si no, usamos CustomerTypeName como fallback
    if (c?.companyName && c.companyName.toString().trim() !== "") return true;

    const type = norm(c?.customerTypeName);
    return (
      type === "JURIDICO" ||
      type === "JURÍDICO" ||
      type === "JURIDICA" ||
      type === "JURÍDICA"
    );
  };

  const buildLabel = (c) => {
    const doc = (c?.documentNumber || "").toString().trim();
    const fullName = (c?.fullName || "").toString().trim();
    const company = (c?.companyName || "").toString().trim();
    const legal = isLegalCustomer(c);

    if (legal) {
      const nitPart = doc ? `NIT ${doc}` : "NIT (sin número)";
      const namePart = company || fullName || "(Sin razón social)";
      return `${nitPart} · ${namePart}`;
    }

    const docPart = doc || "(Sin documento)";
    const namePart = fullName || "(Sin nombre)";
    return `${docPart} · ${namePart}`;
  };

  // -------------------- Opciones (memo) --------------------
  const selectOptions = useMemo(() => {
    return (customers || []).map((c) => {
      const label = buildLabel(c);

      // búsqueda amplia: documento, nombre/empresa, email, tipo
      const searchText = [
        c?.documentNumber,
        c?.fullName,
        c?.companyName,
        c?.email,
        c?.customerTypeName,
        label,
      ]
        .filter(Boolean)
        .map((x) => x.toString().toLowerCase())
        .join(" ");

      return {
        value: c.id,
        label,
        data: c,
        searchText,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return selectOptions.find((o) => o.value === value) || null;
  }, [value, selectOptions]);

  const filterOption = (option, rawInput) => {
    const input = (rawInput || "").trim().toLowerCase();
    if (!input) return true;
    return option?.data?.searchText?.includes(input);
  };

  return (
    <Select
      styles={customStyles}
      placeholder={placeholder}
      isClearable
      isDisabled={isDisabled || loading}
      isLoading={loading}
      menuPortalTarget={document.body}
      options={selectOptions}
      value={selectedOption}
      filterOption={filterOption}
      onChange={(selected) => {
        onChange?.(selected?.value || "");
        onSelectCustomer?.(selected?.data || null);
      }}
      noOptionsMessage={() =>
        loading ? "Cargando clientes..." : "No se encontraron clientes activos"
      }
    />
  );
};

export default SelectCustomer;
